import { useMemo } from 'react'
import { useAuth } from 'react-oidc-context'

import type { Context, Profile } from '@/auth'

const toPrivilegesArray = (raw: unknown): string[] => {
  if (Array.isArray(raw)) return raw as string[]
  if (typeof raw === 'string') return [raw]
  return []
}

export const useAuthContext = (): Context => {
  const auth = useAuth()

  // Map the user profile to your Profile type with computed productionOnly
  const profile: Profile | undefined = useMemo(() => {
    if (!auth.user?.profile) return undefined
    const raw = auth.user.profile?.privileges
    const privileges = toPrivilegesArray(raw)
    const productionOnly = !privileges.includes('pm.service')

    console.log('Auth profile calculation:', {
      privileges,
      productionOnly,
      hasService: privileges?.includes('pm.service'),
    })

    return {
      name: auth.user.profile.name,
      company: auth.user.profile.company,
      picture: auth.user.profile.picture,
      productionOnly,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.user?.profile?.name,
    auth.user?.profile?.company,
    auth.user?.profile?.picture,
    auth.user?.profile?.privileges,
  ])

  // Map auth methods to your Context interface
  const signOut = async (): Promise<void> => {
    try {
      await auth.signoutRedirect()
    } catch (error) {
      console.error('Sign out failed:', error)
      throw error
    }
  }

  const signInOIDC = async (): Promise<void> => {
    try {
      await auth.signinRedirect()
    } catch (error) {
      console.error('Sign in failed:', error)
      throw error
    }
  }

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    profile,
    signOut,
    signInOIDC,
  }
}
